import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { canManageProducts } from "@/lib/permissions";
import { type ChannelListing, readinessLabel, readinessTone, approvalState } from "@/lib/channels";
import { publicationStateMeta } from "@/lib/listing-pricing";
import { approvalLabel, approvalTone, nextAction, providerLabel } from "@/lib/listing-action";
import ListingCard from "@/pages/listings/components/ListingCard";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName } from "@/lib/connector-identity";
import { indexBcListingStorefrontPaths, indexConnectorStorefront, listingLiveLinks } from "@/lib/listing-live-display";
import { ViewLiveLink } from "@/components/feature/ViewLiveLink";
import { canonicalListingProductId, operatorDirtyDomains } from "@/lib/phase1-sync-contract";
import { approvalPendingSummary, lastFullPublishLabel, lastSyncLabel } from "@/lib/listing-approval-semantics";
import {
  listingPriceRowsFromLocalState,
  summarizeListingChannelPrices,
} from "@/lib/listing-channel-price-display";

export default function Listings() {
  const { org, role } = useAuth();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [filterReadiness, setFilterReadiness] = useState("");
  const [search, setSearch] = useState("");

  const canManage = canManageProducts(role);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["listings-index", org?.id, page, pageSize, filterStatus, filterProvider, filterReadiness, search],
    enabled: !!org?.id,
    queryFn: async () => {
      // Resolve the readiness filter to a set of listing ids up front so
      // pagination (count + range) stays server-side and correct.
      let readinessIds: string[] | null = null;
      if (filterReadiness) {
        const { data: readyRows } = await supabase
          .from("listing_readiness_checks")
          .select("channel_listing_id")
          .eq("org_id", org!.id)
          .eq("status", filterReadiness);
        readinessIds = (readyRows ?? []).map((r) => r.channel_listing_id);
        if (readinessIds.length === 0) {
          return { listings: [], total: 0, readinessMap: {}, bcExternal: {}, storefront: { originByConnector: {}, storeHashByConnector: {} }, bcPathByListing: {}, variantsByListing: {} };
        }
      }

      let q = supabase
        .from("channel_listings")
        .select(
          "id, org_id, product_id, connector_id, status, publication_status, dirty_domains, channel_title, channel_category_id, price_strategy, inventory_strategy, media_strategy, last_published_at, last_synced_at, last_error, created_at, updated_at, intended_version, operator_revision, approved_version, published_version, external_listing_id, currency, provider_config, connector:connectors(id, name, provider, auth_status), product:products(id, name, sku, selling_price)",
          { count: "exact" }
        )
        .eq("org_id", org!.id);

      if (filterStatus) q = q.eq("status", filterStatus);
      if (filterProvider) q = q.eq("connectors.provider", filterProvider);
      if (readinessIds) q = q.in("id", readinessIds);
      if (search.trim()) {
        const ilike = `%${search.trim()}%`;
        q = q.or(`channel_title.ilike.${ilike},products.name.ilike.${ilike}`);
      }
      q = q.order("updated_at", { ascending: false }).range(page * pageSize, page * pageSize + pageSize - 1);

      const res = await q;
      const rows = (res.data ?? []) as unknown as ChannelListing[];

      // Fetch readiness for visible listings
      const listingIds = rows.map((r) => r.id);
      let readinessMap: Record<string, string> = {};
      const bcExternal: Record<string, string> = {};
      let bcPathByListing: Record<string, string> = {};
      const variantsByListing: Record<string, Array<{
        channel_listing_id?: string;
        variant_id?: string;
        included?: boolean;
        channel_price?: number | null;
        variants?: { id?: string; sku?: string | null; selling_price?: number | null; option_values?: Record<string, string> | null } | null;
      }>> = {};
      const { data: bcConfigRows } = await supabase
        .from("connector_bigcommerce_config")
        .select("connector_id, storefront_origin, store_hash")
        .eq("org_id", org!.id);
      const storefront = indexConnectorStorefront(
        (bcConfigRows ?? []) as { connector_id: string; storefront_origin: string | null; store_hash: string | null }[],
      );
      if (listingIds.length > 0) {
        const [rdata, bc, clv] = await Promise.all([
          supabase
            .from("listing_readiness_checks")
            .select("channel_listing_id, status")
            .eq("org_id", org!.id)
            .in("channel_listing_id", listingIds),
          supabase
            .from("bigcommerce_products")
            .select("listing_id, external_product_id, storefront_custom_url")
            .eq("org_id", org!.id)
            .in("listing_id", listingIds),
          supabase
            .from("channel_listing_variants")
            .select("channel_listing_id, variant_id, included, channel_price, variants(id, sku, selling_price, option_values)")
            .eq("org_id", org!.id)
            .in("channel_listing_id", listingIds)
            .eq("included", true),
        ]);
        (rdata.data ?? []).forEach((r) => {
          readinessMap[r.channel_listing_id] = r.status;
        });
        for (const row of (bc.data ?? []) as { listing_id: string; external_product_id: number | null }[]) {
          if (row.external_product_id) bcExternal[row.listing_id] = String(row.external_product_id);
        }
        bcPathByListing = indexBcListingStorefrontPaths(
          (bc.data ?? []) as { listing_id: string; storefront_custom_url: string | null }[],
        );
        for (const row of (clv.data ?? []) as {
          channel_listing_id: string;
          variant_id?: string;
          included?: boolean;
          channel_price?: number | null;
          variants?: { id?: string; sku?: string | null; selling_price?: number | null; option_values?: Record<string, string> | null } | null;
        }[]) {
          (variantsByListing[row.channel_listing_id] ??= []).push(row);
        }
      }

      return {
        listings: rows,
        total: res.count ?? 0,
        readinessMap,
        bcExternal,
        storefront,
        bcPathByListing,
        variantsByListing,
      };
    },
  });

  if (isLoading) return <ListSkeleton />;
  if (isError) {
    return (
      <div>
        <PageHeader title="Channel Listings" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Could not load listings.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const listings = data?.listings ?? [];
  const readinessMap = data?.readinessMap ?? {};
  const bcExternal = data?.bcExternal ?? {};
  const originByConnector = data?.storefront.originByConnector ?? {};
  const storeHashByConnector = data?.storefront.storeHashByConnector ?? {};
  const bcPathByListing = data?.bcPathByListing ?? {};
  const variantsByListing = data?.variantsByListing ?? {};

  const providers = Array.from(new Set(listings.map((l) => l.connector?.provider).filter(Boolean)));

  return (
    <div>
      <PageHeader title="Channel Listings" subtitle="Manage product listings across all sales channels">
        {canManage && (
          <Link
            to="/listing-templates"
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-layout-masonry-line" /> Templates
          </Link>
        )}
        {canManage && (
          <Link
            to="/products"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" /> Create from product
          </Link>
        )}
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search title or product..."
              className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="ready">Ready to publish</option>
            <option value="published">Published</option>
            <option value="paused">Paused</option>
            <option value="error">Error</option>
            <option value="ended">Ended</option>
            <option value="archived">Archived</option>
          </select>
          {providers.length > 0 && (
            <select
              value={filterProvider}
              onChange={(e) => { setFilterProvider(e.target.value); setPage(0); }}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="">All providers</option>
              {providers.map((p) => (
                <option key={p} value={p}>{providerLabel(p)}</option>
              ))}
            </select>
          )}
          <select
            value={filterReadiness}
            onChange={(e) => { setFilterReadiness(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          >
            <option value="">All readiness</option>
            <option value="READY">Ready</option>
            <option value="READY_WITH_WARNINGS">Warnings</option>
            <option value="BLOCKED">Blocked</option>
          </select>
        </div>

        {listings.length === 0 ? (
          <EmptyState
            icon="ri-store-2-line"
            title="No channel listings yet"
            description="Create listings from the product detail page to publish to eBay, BigCommerce, Amazon, and more."
          />
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">Channel</th>
                    <th className="px-4 py-3 font-medium">Selling price</th>
                    <th className="px-4 py-3 font-medium">Readiness</th>
                    <th className="px-4 py-3 font-medium">Approval</th>
                    <th className="px-4 py-3 font-medium">Publication</th>
                    <th className="px-4 py-3 font-medium">{lastFullPublishLabel()}</th>
                    <th className="px-4 py-3 font-medium">{lastSyncLabel()}</th>
                    <th className="px-4 py-3 font-medium w-40">Next action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {listings
                    .map((l) => {
                      const rdy = readinessMap[l.id];
                      const approval = approvalState(l);
                      const confirmedId = bcExternal[l.id] ?? l.external_listing_id;
                      const connectionReady = connectorIsReady(l.connector?.auth_status);
                      const operatorDirty = operatorDirtyDomains(l.dirty_domains);
                      const action = nextAction({
                        readiness: rdy,
                        approval,
                        status: l.status,
                        publicationStatus: l.publication_status,
                        hasDirty: operatorDirty.length > 0,
                        dirtyDomains: l.dirty_domains,
                        connectionReady,
                        providerConfirmedListed: Boolean(confirmedId),
                      });
                      const approvalCls = approvalTone(approval) === "approved"
                        ? "bg-emerald-50 text-emerald-700"
                        : approvalTone(approval) === "warning"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-slate-100 text-slate-600";
                      const actionTone = action?.tone ?? "neutral";
                      const actionCls = {
                        danger: "text-red-600 hover:text-red-700",
                        warning: "text-amber-600 hover:text-amber-700",
                        primary: "text-emerald-600 hover:text-emerald-700",
                        neutral: "text-slate-500 hover:text-slate-700",
                      }[actionTone];
                      const actionIcon = {
                        danger: "ri-alert-line",
                        warning: "ri-arrow-right-line",
                        primary: "ri-store-2-line",
                        neutral: "ri-arrow-right-line",
                      }[actionTone];
                      const priceRows = listingPriceRowsFromLocalState({
                        priceStrategy: l.price_strategy,
                        providerConfig: l.provider_config,
                        productSellingPrice: l.product?.selling_price ?? null,
                        variants: variantsByListing[l.id] ?? [],
                      });
                      const priceSummary = summarizeListingChannelPrices({
                        rows: priceRows,
                        pricingDirty: (l.dirty_domains ?? []).includes("pricing"),
                        currency: l.currency,
                      });
                      return (
                        <tr key={l.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              {canonicalListingProductId(l) ? (
                                <Link to={`/products/${canonicalListingProductId(l)}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 truncate block">
                                  {l.product?.name ?? "—"}
                                </Link>
                              ) : (
                                <span className="text-sm font-medium text-slate-800 truncate block">{l.product?.name ?? "—"}</span>
                              )}
                              <span className="text-xs text-slate-400 font-mono">{l.product?.sku ?? "—"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-slate-700">{connectorDisplayName({
                              id: l.connector_id,
                              name: l.connector?.name ?? "",
                              provider: l.connector?.provider ?? null,
                              authStatus: l.connector?.auth_status,
                              storefrontOrigin: originByConnector[l.connector_id],
                              storeHash: storeHashByConnector[l.connector_id],
                            })}</div>
                            <span className="text-xs text-slate-400">{providerLabel(l.connector?.provider)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/listings/${l.id}?tab=pricing`} className="block min-w-0 hover:text-emerald-700">
                              <span className="text-sm text-slate-800 whitespace-nowrap">{priceSummary.label}</span>
                              {priceSummary.overrideCount > 0 && (
                                <span className="block text-[11px] text-amber-700">
                                  {priceSummary.overrideCount === 1 ? "Variant override" : `${priceSummary.overrideCount} overrides`}
                                </span>
                              )}
                              {priceSummary.pendingLabel && (
                                <span className="block text-[11px] text-amber-700">{priceSummary.pendingLabel}</span>
                              )}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            {rdy ? (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                readinessTone(rdy) === "ready"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : readinessTone(rdy) === "warning"
                                  ? "bg-amber-50 text-amber-700"
                                  : readinessTone(rdy) === "blocked"
                                  ? "bg-red-50 text-red-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}>
                                {readinessLabel(rdy)}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Not evaluated</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${approvalCls}`}>
                              {approvalLabel(approval)}
                            </span>
                            {approval === "stale" && approvalPendingSummary(l.dirty_domains) && (
                              <p className="text-[11px] text-amber-700 mt-0.5">{approvalPendingSummary(l.dirty_domains)}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const signal = publicationStateMeta({
                                publicationStatus: l.publication_status,
                                status: l.status,
                                hasDirty: operatorDirty.length > 0,
                                connectionReady,
                                lastError: l.last_error,
                                providerConfirmedListed: Boolean(confirmedId),
                              });
                              const tone = {
                                green: "text-emerald-700",
                                amber: "text-amber-700",
                                red: "text-red-700",
                                grey: "text-slate-400",
                              }[signal.signal];
                              return (
                                <span className="inline-flex items-center gap-1.5" title={signal.description}>
                                  <i className={`${signal.icon} ${tone} text-sm`} />
                                  <span className={`text-xs font-medium ${tone}`}>{signal.label}</span>
                                  <ViewLiveLink href={listingLiveLinks({
                                    provider: l.connector?.provider,
                                    externalId: confirmedId,
                                    storefrontOrigin: originByConnector[l.connector_id] ?? originByConnector[l.connector?.id ?? ""],
                                    customUrl: bcPathByListing[l.id],
                                  }).liveUrl} />
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {l.last_published_at
                              ? new Date(l.last_published_at).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {l.last_synced_at
                              ? new Date(l.last_synced_at).toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {action ? (
                              <Link to={`/listings/${l.id}`} className={`inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap ${actionCls}`}>
                                <i className={actionIcon} />
                                {action.label}
                              </Link>
                            ) : (
                              <Link to={`/listings/${l.id}`} className="text-xs font-medium text-slate-400 hover:text-slate-600 whitespace-nowrap">
                                View
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3">
              {listings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  readiness={readinessMap[l.id]}
                  confirmedId={bcExternal[l.id] ?? l.external_listing_id}
                  storefrontOrigin={originByConnector[l.connector_id] ?? originByConnector[l.connector?.id ?? ""]}
                  storeHash={storeHashByConnector[l.connector_id]}
                  customUrl={bcPathByListing[l.id]}
                  sellingPrice={summarizeListingChannelPrices({
                    rows: listingPriceRowsFromLocalState({
                      priceStrategy: l.price_strategy,
                      providerConfig: l.provider_config,
                      productSellingPrice: l.product?.selling_price ?? null,
                      variants: variantsByListing[l.id] ?? [],
                    }),
                    pricingDirty: (l.dirty_domains ?? []).includes("pricing"),
                    currency: l.currency,
                  })}
                />
              ))}
            </div>
            <Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
          </>
        )}
      </div>
    </div>
  );
}